<template>
  <div class="roulette-game">
    <!-- Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-roulette" :size="18" /> {{ $t('games.roulette.title') }}
        <span class="game-tag roulette-tag">{{ $t('games.roulette.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body: main area (wheel + table) + sidebar -->
    <div class="game-body">
      <!-- ── Main area (wheel + betting table) ────────────────────────────────── -->
      <div class="main-area">
        <!-- Wheel Section -->
        <div class="wheel-section" ref="canvasWrap">
          <canvas ref="wheelCanvas" class="wheel-canvas" />

          <!-- Marker triangle (static, always at top) -->
          <div class="wheel-marker-overlay" ref="markerOverlay">
            <div class="wheel-marker-triangle"></div>
          </div>

          <!-- Result overlay -->
          <Transition name="overlay-fade">
            <div v-if="resultVisible" class="result-overlay">
              <div class="result-number-badge" :class="lastResult?.color">
                {{ lastResult?.label ?? '' }}
              </div>
              <div class="result-color-name">{{ resultColorName }}</div>
              <div
                class="result-amount"
                :class="{ win: lastNetCents > 0, loss: lastNetCents < 0, push: lastNetCents === 0 }"
              >
                {{ resultAmountText }}
              </div>
              <button class="btn-spin-again" @click="resetState">{{ $t('games.spin_again') }}</button>
            </div>
          </Transition>

          <!-- History strip (shows last 20; click to see detail) -->
          <div class="history-strip">
            <div
              v-for="(entry, i) in historyStripEntries"
              :key="i"
              class="hist-dot"
              :class="[entry.color, { 'hist-dot-selected': selectedHistoryIdx === i }]"
              :title="`${entry.label} (${entry.color}) — Spin #${history.length - i}`"
              @click="selectedHistoryIdx = selectedHistoryIdx === i ? null : i"
            >
              {{ entry.label }}
            </div>
          </div>

          <!-- Clicked history entry detail -->
          <Transition name="hist-detail-fade">
            <div v-if="selectedHistoryIdx !== null && history[selectedHistoryIdx]" class="hist-detail-bar">
              <div class="hist-detail-badge" :class="history[selectedHistoryIdx]!.color">
                {{ history[selectedHistoryIdx]!.label }}
              </div>
              <span class="hist-detail-info">
                {{ history[selectedHistoryIdx]!.color }} &middot; Spin #{{ history.length - selectedHistoryIdx }}
              </span>
              <button class="hist-detail-close" @click="selectedHistoryIdx = null">×</button>
            </div>
          </Transition>
        </div>

        <!-- ── View toggle bar (Table / Racetrack) ──────────────────────────────── -->
        <div class="view-toggle-bar">
          <button
            class="view-btn"
            :class="{ active: !showRacetrack }"
            @click="showRacetrack = false"
          >{{ $t('games.roulette.table_view') }}</button>
          <button
            class="view-btn"
            :class="{ active: showRacetrack }"
            @click="showRacetrack = true"
          >{{ $t('games.roulette.racetrack_view') }}</button>
        </div>

        <!-- ── Betting Table Section ───────────────────────────────────────────── -->
        <div v-if="!showRacetrack" class="table-section">
          <div class="betting-table">
            <!-- ── Zero cell ──────────────────────────────────────────────────── -->
            <div
              class="bet-cell zero-cell"
              style="grid-column: 1; grid-row: 2 / 5"
              @click="addBet('straight', [0])"
            >
              <span class="cell-label green">0</span>
              <div v-if="betAt('straight', [0]) > 0" class="chip-badge">
                {{ fmtChip(betAt('straight', [0])) }}
              </div>
            </div>

            <!-- ── Street row: one zone per number-column (+ six-line at right edge) ── -->
            <div
              v-for="col in 12"
              :key="`street-${col}`"
              class="street-zone"
              :style="{ gridColumn: col + 1, gridRow: 1 }"
              @click.self="addBet('street', streetNums(col))"
              :title="$t('games.roulette.street_bet', { nums: streetNums(col).join('-') })"
            >
              <div v-if="betAt('street', streetNums(col)) > 0" class="chip-badge sm">
                {{ fmtChip(betAt('street', streetNums(col))) }}
              </div>
              <!-- Six-line zone at right edge of street zone (if not last col) -->
              <div
                v-if="col < 12"
                class="sixline-zone"
                @click.stop="addBet('six_line', sixlineNums(col))"
                :title="$t('games.roulette.six_line_bet', { nums: sixlineNums(col).join('-') })"
              >
                <div v-if="betAt('six_line', sixlineNums(col)) > 0" class="chip-badge xs">
                  {{ fmtChip(betAt('six_line', sixlineNums(col))) }}
                </div>
              </div>
            </div>

            <!-- ── Number cells (3 rows × 12 cols) ───────────────────────────── -->
            <template v-for="col in 12" :key="`col-${col}`">
              <div
                v-for="(num, rowIdx) in colCells(col)"
                :key="num"
                class="bet-cell number-cell"
                :class="pocketColor(num)"
                :style="{ gridColumn: col + 1, gridRow: rowIdx + 2 }"
                @click.self="addBet('straight', [num])"
              >
                <span class="cell-label">{{ num }}</span>

                <!-- Straight bet chip -->
                <div v-if="betAt('straight', [num]) > 0" class="chip-badge">
                  {{ fmtChip(betAt('straight', [num])) }}
                </div>

                <!-- Right split zone (horizontal, with right column neighbor) -->
                <div
                  v-if="col < 12"
                  class="zone-right"
                  @click.stop="addBet('split', splitHNums(col, rowIdx))"
                  :title="`Split ${splitHNums(col, rowIdx).join('-')} (17:1)`"
                >
                  <div v-if="betAt('split', splitHNums(col, rowIdx)) > 0" class="chip-badge xs">
                    {{ fmtChip(betAt('split', splitHNums(col, rowIdx))) }}
                  </div>
                </div>

                <!-- Bottom split zone (vertical, with row below neighbor) -->
                <div
                  v-if="rowIdx < 2"
                  class="zone-bottom"
                  @click.stop="addBet('split', splitVNums(col, rowIdx))"
                  :title="`Split ${splitVNums(col, rowIdx).join('-')} (17:1)`"
                >
                  <div v-if="betAt('split', splitVNums(col, rowIdx)) > 0" class="chip-badge xs">
                    {{ fmtChip(betAt('split', splitVNums(col, rowIdx))) }}
                  </div>
                </div>

                <!-- Bottom-right corner zone (4-number corner) -->
                <div
                  v-if="col < 12 && rowIdx < 2"
                  class="zone-corner"
                  @click.stop="addBet('corner', cornerNums(col, rowIdx))"
                  :title="`Corner ${cornerNums(col, rowIdx).join('-')} (8:1)`"
                >
                  <div v-if="betAt('corner', cornerNums(col, rowIdx)) > 0" class="chip-badge xs">
                    {{ fmtChip(betAt('corner', cornerNums(col, rowIdx))) }}
                  </div>
                </div>
              </div>
            </template>

            <!-- ── Column bet cells (right side, one per grid row 2-4) ─────────── -->
            <div
              class="bet-cell col-bet-cell"
              style="grid-column: 14; grid-row: 2"
              @click="addBet('column_3', colNums(3))"
              title="Column (3,6,9,…,36) 2:1"
            >
              <span class="cell-label sm">2:1</span>
              <div v-if="betAt('column_3', colNums(3)) > 0" class="chip-badge">
                {{ fmtChip(betAt('column_3', colNums(3))) }}
              </div>
            </div>
            <div
              class="bet-cell col-bet-cell"
              style="grid-column: 14; grid-row: 3"
              @click="addBet('column_2', colNums(2))"
              title="Column (2,5,8,…,35) 2:1"
            >
              <span class="cell-label sm">2:1</span>
              <div v-if="betAt('column_2', colNums(2)) > 0" class="chip-badge">
                {{ fmtChip(betAt('column_2', colNums(2))) }}
              </div>
            </div>
            <div
              class="bet-cell col-bet-cell"
              style="grid-column: 14; grid-row: 4"
              @click="addBet('column_1', colNums(1))"
              title="Column (1,4,7,…,34) 2:1"
            >
              <span class="cell-label sm">2:1</span>
              <div v-if="betAt('column_1', colNums(1)) > 0" class="chip-badge">
                {{ fmtChip(betAt('column_1', colNums(1))) }}
              </div>
            </div>

            <!-- ── Dozen row ──────────────────────────────────────────────────── -->
            <div
              class="bet-cell dozen-cell"
              style="grid-column: 2 / 6; grid-row: 5"
              @click="addBet('dozen_1', dozenNums(1))"
              title="1st Dozen (1-12) 2:1"
            >
              <span class="cell-label sm">1st 12</span>
              <div v-if="betAt('dozen_1', dozenNums(1)) > 0" class="chip-badge">
                {{ fmtChip(betAt('dozen_1', dozenNums(1))) }}
              </div>
            </div>
            <div
              class="bet-cell dozen-cell"
              style="grid-column: 6 / 10; grid-row: 5"
              @click="addBet('dozen_2', dozenNums(2))"
              title="2nd Dozen (13-24) 2:1"
            >
              <span class="cell-label sm">2nd 12</span>
              <div v-if="betAt('dozen_2', dozenNums(2)) > 0" class="chip-badge">
                {{ fmtChip(betAt('dozen_2', dozenNums(2))) }}
              </div>
            </div>
            <div
              class="bet-cell dozen-cell"
              style="grid-column: 10 / 14; grid-row: 5"
              @click="addBet('dozen_3', dozenNums(3))"
              title="3rd Dozen (25-36) 2:1"
            >
              <span class="cell-label sm">3rd 12</span>
              <div v-if="betAt('dozen_3', dozenNums(3)) > 0" class="chip-badge">
                {{ fmtChip(betAt('dozen_3', dozenNums(3))) }}
              </div>
            </div>

            <!-- ── Even-money row ─────────────────────────────────────────────── -->
            <div
              class="bet-cell outside-cell"
              style="grid-column: 2 / 4; grid-row: 6"
              @click="addBet('low', lowNums())"
              title="1-18 (Low) 1:1"
            >
              <span class="cell-label sm">1-18</span>
              <div v-if="betAt('low', lowNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('low', lowNums())) }}
              </div>
            </div>
            <div
              class="bet-cell outside-cell"
              style="grid-column: 4 / 6; grid-row: 6"
              @click="addBet('even', evenNums())"
              title="Even 1:1"
            >
              <span class="cell-label sm">Even</span>
              <div v-if="betAt('even', evenNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('even', evenNums())) }}
              </div>
            </div>
            <div
              class="bet-cell outside-cell outside-red"
              style="grid-column: 6 / 8; grid-row: 6"
              @click="addBet('red', redNums())"
              title="Red 1:1"
            >
              <span class="red-diamond">◆</span>
              <div v-if="betAt('red', redNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('red', redNums())) }}
              </div>
            </div>
            <div
              class="bet-cell outside-cell outside-black"
              style="grid-column: 8 / 10; grid-row: 6"
              @click="addBet('black', blackNums())"
              title="Black 1:1"
            >
              <span class="black-diamond">◆</span>
              <div v-if="betAt('black', blackNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('black', blackNums())) }}
              </div>
            </div>
            <div
              class="bet-cell outside-cell"
              style="grid-column: 10 / 12; grid-row: 6"
              @click="addBet('odd', oddNums())"
              title="Odd 1:1"
            >
              <span class="cell-label sm">Odd</span>
              <div v-if="betAt('odd', oddNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('odd', oddNums())) }}
              </div>
            </div>
            <div
              class="bet-cell outside-cell"
              style="grid-column: 12 / 14; grid-row: 6"
              @click="addBet('high', highNums())"
              title="19-36 (High) 1:1"
            >
              <span class="cell-label sm">19-36</span>
              <div v-if="betAt('high', highNums()) > 0" class="chip-badge">
                {{ fmtChip(betAt('high', highNums())) }}
              </div>
            </div>
          </div>
        </div>

        <!-- ── Racetrack section ──────────────────────────────────────────────── -->
        <div v-else class="racetrack-section">

          <!-- Zone preset buttons -->
          <div class="track-presets">
            <button
              class="preset-btn preset-voisins"
              :disabled="spinning"
              @click="addVoisins"
              @mouseenter="hoveredPreset = 'voisins'"
              @mouseleave="hoveredPreset = null"
              title="Voisins du Zéro: 17 numbers around 0 (9 chips)"
            >
              Voisins (9)
            </button>
            <button
              class="preset-btn preset-tiers"
              :disabled="spinning"
              @click="addTiers"
              @mouseenter="hoveredPreset = 'tiers'"
              @mouseleave="hoveredPreset = null"
              title="Tiers du Cylindre: 12 numbers opposite 0 (6 chips)"
            >
              Tiers (6)
            </button>
            <button
              class="preset-btn preset-orphelins"
              :disabled="spinning"
              @click="addOrphelins"
              @mouseenter="hoveredPreset = 'orphelins'"
              @mouseleave="hoveredPreset = null"
              title="Orphelins: 8 remaining numbers (5 chips)"
            >
              Orphelins (5)
            </button>
          </div>

          <!-- Racetrack oval (two-row layout following wheel order) -->
          <div class="racetrack-oval">
            <!-- Top row: EUROPEAN_ORDER indices 0-18 (left to right) -->
            <div class="track-row">
              <button
                v-for="num in topRow"
                :key="`top-${num}`"
                class="track-num"
                :style="trackNumStyle(num)"
                :disabled="spinning"
                @click="handleTrackClick(num)"
                @mouseenter="hoveredTrackNum = num"
                @mouseleave="hoveredTrackNum = null"
                :title="`${num} — click to bet neighbors (±2)`"
              >{{ num }}</button>
            </div>
            <!-- Bottom row: EUROPEAN_ORDER indices 36-19 reversed (right to left) -->
            <div class="track-row">
              <button
                v-for="num in bottomRow"
                :key="`bot-${num}`"
                class="track-num"
                :style="trackNumStyle(num)"
                :disabled="spinning"
                @click="handleTrackClick(num)"
                @mouseenter="hoveredTrackNum = num"
                @mouseleave="hoveredTrackNum = null"
                :title="`${num} — click to bet neighbors (±2)`"
              >{{ num }}</button>
            </div>
          </div>

          <!-- Legend and tip -->
          <div class="track-legend">
            <div class="legend-items">
              <span class="leg-item"><span class="leg-dot" style="background:var(--roulette-voisins,#c2185b)"></span>Voisins</span>
              <span class="leg-item"><span class="leg-dot" style="background:var(--roulette-tiers,#1565c0)"></span>Tiers</span>
              <span class="leg-item"><span class="leg-dot" style="background:var(--roulette-orphelins,#6a1b9a)"></span>Orphelins</span>
              <span class="leg-item"><span class="leg-dot" style="background:var(--accent-gold,#ffd700)"></span>Straight</span>
            </div>
            <div class="leg-tip">Click a number to bet it + 2 neighbors on each side</div>
          </div>
        </div>
      </div>

      <!-- ── Right Sidebar ──────────────────────────────────────────────────── -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >
            {{ $t('games.bet_tab') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'stats' }"
            @click="activeTab = 'stats'"
          >
            Stats
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >
            {{ $t('games.rules_tab') }}
          </button>
        </div>

        <!-- BET TAB -->
        <div v-show="activeTab === 'bet'" class="game-tab-content">
          <!-- Chip selector -->
          <label class="ctrl-label">Chip Value</label>
          <div class="chip-row">
            <button
              v-for="chip in CHIPS"
              :key="chip.value"
              class="chip"
              :class="[chip.colorClass, { selected: selectedChipCents === chip.value }]"
              :disabled="spinning"
              @click="selectedChipCents = chip.value"
            >
              {{ chip.label }}
            </button>
          </div>

          <!-- Total bet + potential win -->
          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">Total Bet</div>
              <div class="sp-value">{{ totalBetDisplay }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Bets Placed</div>
              <div class="sp-value gold">{{ placedBets.size }}</div>
            </div>
          </div>

          <!-- Undo / Clear / Rebet / Double -->
          <div class="action-row">
            <button
              class="act-btn"
              :disabled="betHistory.length === 0 || spinning"
              @click="undoBet"
              title="Remove last placed chip"
            >
              Undo
            </button>
            <button
              class="act-btn act-btn-danger"
              :disabled="placedBets.size === 0 || spinning"
              @click="clearBets"
              title="Remove all bets"
            >
              Clear
            </button>
            <button
              class="act-btn act-btn-gold"
              :disabled="savedBets.size === 0 || spinning"
              @click="rebet"
              title="Repeat last spin's bets"
            >
              Rebet
            </button>
            <button
              class="act-btn act-btn-double"
              :disabled="placedBets.size === 0 || spinning"
              @click="doubleBets"
              title="Double all current bets (×2)"
            >
              ×2
            </button>
          </div>

          <!-- Insufficient balance warning -->
          <div v-if="insufficientBalance" class="warn-msg">
            {{ $t('games.roulette.insufficient_balance_msg', { amount: formatCurrency(totalBetCents, rouletteCurrency) }) }}
          </div>

          <!-- Table limit warning -->
          <div v-else-if="tableLimitExceeded" class="warn-msg">
            {{ $t('games.roulette.table_limit_message', { amount: (TABLE_LIMIT_CENTS / 100).toFixed(0) }) }}
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>

          <!-- Bet limits indicator -->
          <div class="limit-bar">
            <span class="limit-label">Min / Max</span>
            <span class="limit-value">${{ (MIN_BET_CENTS / 100).toFixed(0) }} / ${{ (MAX_BET_PER_ZONE_CENTS / 100).toFixed(0) }}</span>
          </div>
          <div class="limit-bar">
            <span class="limit-label">{{ $t('games.roulette.table_limit') }}</span>
            <span class="limit-value" :class="{ 'limit-exceeded': tableLimitExceeded }">${{ (TABLE_LIMIT_CENTS / 100).toFixed(0) }}</span>
          </div>

          <button
            class="btn-action spin-btn"
            :disabled="!canSpin"
            @click="doSpin"
          >
            <span v-if="spinning" class="spin-loading">Spinning...</span>
            <span v-else>SPIN</span>
          </button>

          <div class="balance-row">
            <span class="bal-label">Balance</span>
            <span class="bal-value green">{{ balanceDisplay }}</span>
          </div>
        </div>

        <!-- STATS TAB -->
        <div v-show="activeTab === 'stats'" class="game-tab-content">
          <div v-if="!history.length" class="stats-empty">
            No spins yet. Play a few rounds to see statistics.
          </div>
          <template v-else>
            <div class="stats-header">
              <span class="ctrl-label">Last {{ history.length }} spin{{ history.length !== 1 ? 's' : '' }}</span>
            </div>

            <!-- Color distribution -->
            <div class="stats-section-label">Color Distribution</div>
            <div class="stats-color-row">
              <div class="stats-color-pill">
                <span class="scp-color-dot" style="background:var(--roulette-red-dark,#b71c1c)"></span>
                <span class="scp-label">Red</span>
                <span class="scp-value">{{ pRed.toFixed(1) }}%</span>
              </div>
              <div class="stats-color-pill">
                <span class="scp-color-dot" style="background:var(--roulette-black-dark,#444)"></span>
                <span class="scp-label">Black</span>
                <span class="scp-value">{{ pBlack.toFixed(1) }}%</span>
              </div>
              <div class="stats-color-pill">
                <span class="scp-color-dot" style="background:var(--roulette-green-dark,#1b6b1b)"></span>
                <span class="scp-label">Zero</span>
                <span class="scp-value">{{ pGreen.toFixed(1) }}%</span>
              </div>
            </div>

            <!-- Hot numbers -->
            <div class="stats-section-label">🔥 Hot Numbers</div>
            <div class="stats-num-grid">
              <div
                v-for="stat in hotNumbers"
                :key="stat.pocket"
                class="stats-num-item"
                :class="stat.color"
                :title="`${stat.label} — appeared ${stat.count} time${stat.count !== 1 ? 's' : ''}`"
              >
                <span class="sni-label">{{ stat.label }}</span>
                <span class="sni-count">×{{ stat.count }}</span>
              </div>
            </div>

            <!-- Cold numbers -->
            <div class="stats-section-label">❄️ Cold Numbers</div>
            <div class="stats-num-grid">
              <div
                v-for="stat in coldNumbers"
                :key="stat.pocket"
                class="stats-num-item stats-num-cold"
                :class="stat.color"
                :title="`${stat.label} — appeared ${stat.count} time${stat.count !== 1 ? 's' : ''}`"
              >
                <span class="sni-label">{{ stat.label }}</span>
                <span class="sni-count">×{{ stat.count }}</span>
              </div>
            </div>

            <!-- Responsible gaming disclaimer -->
            <div class="stats-disclaimer">
              Past results do not predict future outcomes. Each spin is independent and provably fair. Please play responsibly.
            </div>
          </template>
        </div>

        <!-- RULES TAB -->
        <div v-show="activeTab === 'rules'" class="game-tab-content">
          <div class="rules-section">
            <div class="rules-title"><span class="rules-title-icon"><AppIcon name="game-roulette" :size="18" /></span> Bet Types</div>
            <ul class="rules-list">
              <li class="rules-item"><strong>Straight (35:1)</strong> — single number</li>
              <li class="rules-item"><strong>Split (17:1)</strong> — 2 adjacent numbers</li>
              <li class="rules-item">{{ $t('games.roulette.rule_street') }}</li>
              <li class="rules-item"><strong>Corner (8:1)</strong> — 4 numbers in a square</li>
              <li class="rules-item">{{ $t('games.roulette.rule_six_line') }}</li>
              <li class="rules-item"><strong>Dozen / Column (2:1)</strong> — 12 numbers</li>
              <li class="rules-item"><strong>Even Money (1:1)</strong> — Red/Black, Odd/Even, 1-18/19-36</li>
            </ul>
            <div class="rules-note">
              Click any cell to place your selected chip. Hover near cell borders for split, corner, and six-line zones.
              European wheel: 37 pockets (0–36) — 2.70% house edge.
            </div>
          </div>
          <div class="fair-tag"><AppIcon name="provably-fair" :size="12" /> Provably Fair Game</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { formatCurrency, formatNumber } from '@/utils/formatCurrency'

const { t } = useI18n()
const router = useRouter()

// ─── Wheel constants ───────────────────────────────────────────────────────────

/** European roulette pocket order (clockwise from 12 o'clock position). */
const EUROPEAN_ORDER: number[] = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30,
  8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7,
  28, 12, 35, 3, 26,
]

/** Red pockets on a standard roulette wheel. */
const RED_POCKETS: number[] = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]

/** Pocket color by number. */
function pocketColor(n: number): 'green' | 'red' | 'black' {
  if (n === 0) return 'green'
  return RED_POCKETS.includes(n) ? 'red' : 'black'
}

// ─── Racetrack constants ───────────────────────────────────────────────────────

/** Numbers covered by Voisins du Zéro (17 numbers centered on 0). */
const VOISINS_NUMS: readonly number[] = [0, 2, 3, 4, 7, 12, 15, 18, 19, 21, 22, 25, 26, 28, 29, 32, 35]

/** Numbers covered by Tiers du Cylindre (12 numbers opposite 0). */
const TIERS_NUMS: readonly number[] = [5, 8, 10, 11, 13, 16, 23, 24, 27, 30, 33, 36]

/** Numbers covered by Orphelins (8 numbers not in Voisins or Tiers). */
const ORPHELINS_NUMS: readonly number[] = [1, 6, 9, 14, 17, 20, 31, 34]

// ─── Racetrack state ──────────────────────────────────────────────────────────

const showRacetrack = ref<boolean>(false)
const hoveredTrackNum = ref<number | null>(null)
const hoveredPreset = ref<'voisins' | 'tiers' | 'orphelins' | null>(null)

/** Top row of racetrack (indices 0-18 of EUROPEAN_ORDER, displayed left to right). */
const topRow = computed<number[]>(() => EUROPEAN_ORDER.slice(0, 19))

/** Bottom row of racetrack (indices 19-36 reversed, displayed right-to-left). */
const bottomRow = computed<number[]>(() => [...EUROPEAN_ORDER.slice(19)].reverse())

/** The hovered number + 2 neighbors on each side (5 total, by wheel order). */
const hoveredNeighborNums = computed<number[]>(() => {
  if (hoveredTrackNum.value === null) return []
  const idx = EUROPEAN_ORDER.indexOf(hoveredTrackNum.value)
  const n = EUROPEAN_ORDER.length
  return [-2, -1, 0, 1, 2].map((o) => EUROPEAN_ORDER[(idx + o + n) % n])
})

/** Numbers highlighted by the currently hovered preset button. */
const hoveredPresetNums = computed<number[]>(() => {
  if (hoveredPreset.value === 'voisins') return [...VOISINS_NUMS]
  if (hoveredPreset.value === 'tiers') return [...TIERS_NUMS]
  if (hoveredPreset.value === 'orphelins') return [...ORPHELINS_NUMS]
  return []
})

/** Returns inline style for a racetrack number button based on current state. */
function trackNumStyle(num: number): Record<string, string> {
  const presetColors: Record<string, string> = {
    voisins: '#c2185b',
    tiers: '#1565c0',
    orphelins: '#6a1b9a',
  }

  if (hoveredNeighborNums.value.includes(num)) {
    return { background: '#ffd700', color: '#000', borderColor: '#ffd700', transform: 'scale(1.2)', zIndex: '2' }
  }

  const hp = hoveredPreset.value
  if (hp !== null && hoveredPresetNums.value.includes(num)) {
    return { background: presetColors[hp], color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }
  }

  if (betAt('voisins', []) > 0 && VOISINS_NUMS.includes(num)) {
    return { background: '#c2185b', color: '#fff', borderColor: '#c2185b' }
  }
  if (betAt('tiers', []) > 0 && TIERS_NUMS.includes(num)) {
    return { background: '#1565c0', color: '#fff', borderColor: '#1565c0' }
  }
  if (betAt('orphelins', []) > 0 && ORPHELINS_NUMS.includes(num)) {
    return { background: '#6a1b9a', color: '#fff', borderColor: '#6a1b9a' }
  }
  if (betAt('straight', [num]) > 0) {
    return { background: '#ffd700', color: '#000', borderColor: '#ffd700' }
  }

  const c = pocketColor(num)
  if (c === 'green') return { background: '#1b6b1b', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }
  if (c === 'red') return { background: '#7f1010', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }
  return { background: '#1a1a1a', color: '#fff', borderColor: 'rgba(255,255,255,0.15)' }
}

/** Place straight bets on a number and the 2 neighbors on each side (5 total). */
function handleTrackClick(num: number): void {
  if (spinning.value) return
  const idx = EUROPEAN_ORDER.indexOf(num)
  const n = EUROPEAN_ORDER.length
  const neighbors = [-2, -1, 0, 1, 2].map((o) => EUROPEAN_ORDER[(idx + o + n) % n])
  for (const nb of neighbors) {
    addBet('straight', [nb])
  }
}

/** Place a Voisins du Zéro bet (9 chips via backend expansion). */
function addVoisins(): void {
  if (spinning.value) return
  addBet('voisins', [])
}

/** Place a Tiers du Cylindre bet (6 chips via backend expansion). */
function addTiers(): void {
  if (spinning.value) return
  addBet('tiers', [])
}

/** Place an Orphelins bet (5 chips via backend expansion). */
function addOrphelins(): void {
  if (spinning.value) return
  addBet('orphelins', [])
}

// Canvas fill colors for sectors
const SECTOR_COLORS = {
  green: '#1b6b1b',
  red: '#b71c1c',
  black: '#111111',
}

// ─── Animation constants ───────────────────────────────────────────────────────

const SPIN_DURATION_MS = 4200
const BALL_ROTATIONS = 8   // Ball completes 8 counterclockwise rotations
const WHEEL_ROTATIONS = 5  // Wheel completes 5 clockwise rotations before final angle

// Ball radius fractions of wheelRadius
const BALL_TRACK_FRAC = 0.87   // Orbit radius (fraction of wheelRadius)
const BALL_POCKET_FRAC = 0.70  // Final settled radius (fraction of wheelRadius)

// Easing functions
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4)
}

/** Mild overshoot ease-out — ball "bumps" as it settles. */
function easeOutBack(t: number, overshoot = 1.4): number {
  const c1 = overshoot
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

// ─── Chip denominations ────────────────────────────────────────────────────────

interface ChipDef {
  value: number   // cents
  label: string
  colorClass: string
}

const CHIPS: ChipDef[] = [
  { value: 100,    label: '$1',   colorClass: 'chip-1' },
  { value: 500,    label: '$5',   colorClass: 'chip-5' },
  { value: 2500,   label: '$25',  colorClass: 'chip-25' },
  { value: 10000,  label: '$100', colorClass: 'chip-100' },
  { value: 50000,  label: '$500', colorClass: 'chip-500' },
  { value: 100000, label: '$1K',  colorClass: 'chip-1k' },
]

// ─── Table limits ─────────────────────────────────────────────────────────────

/** Minimum bet per zone in cents ($1.00). */
const MIN_BET_CENTS = 100

/** Maximum bet per zone in cents ($1,000.00). */
const MAX_BET_PER_ZONE_CENTS = 100_000

/** Maximum total of all bets per spin in cents ($5,000.00). */
const TABLE_LIMIT_CENTS = 500_000

// ─── Bet state ────────────────────────────────────────────────────────────────

interface PlacedBet {
  type: string
  numbers: number[]
  amountCents: number
}

interface BetHistoryEntry {
  key: string
  chipCents: number
}

/** Canonical key for a bet (type + sorted numbers). */
function betKey(type: string, numbers: number[]): string {
  return `${type}:${[...numbers].sort((a, b) => a - b).join(',')}`
}

const selectedChipCents = ref<number>(500) // $5 default

// Map of betKey → PlacedBet; reassigned on each mutation for Vue reactivity.
const placedBets = ref<Map<string, PlacedBet>>(new Map())
const savedBets = ref<Map<string, PlacedBet>>(new Map())   // for rebet
const betHistory = ref<BetHistoryEntry[]>([])               // for undo

function addBet(type: string, numbers: number[]): void {
  if (spinning.value) return

  const key = betKey(type, numbers)
  const sorted = [...numbers].sort((a, b) => a - b)
  const existing = placedBets.value.get(key)
  const currentZoneAmt = existing?.amountCents ?? 0
  const newZoneAmt = currentZoneAmt + selectedChipCents.value

  // Enforce max bet per zone
  if (newZoneAmt > MAX_BET_PER_ZONE_CENTS) {
    error.value = `Max $${(MAX_BET_PER_ZONE_CENTS / 100).toFixed(0)} per zone`
    return
  }

  // Enforce table limit
  const newTotal = totalBetCents.value - currentZoneAmt + newZoneAmt
  if (TABLE_LIMIT_CENTS > 0 && newTotal > TABLE_LIMIT_CENTS) {
    error.value = `Table limit: ${formatNumber(TABLE_LIMIT_CENTS / 100, undefined, { maximumFractionDigits: 0 })}`
    return
  }

  const newMap = new Map(placedBets.value)
  newMap.set(key, {
    type,
    numbers: sorted,
    amountCents: newZoneAmt,
  })
  placedBets.value = newMap
  betHistory.value.push({ key, chipCents: selectedChipCents.value })
  error.value = null
}

function betAt(type: string, numbers: number[]): number {
  const key = betKey(type, numbers)
  return placedBets.value.get(key)?.amountCents ?? 0
}

function undoBet(): void {
  if (betHistory.value.length === 0) return
  const last = betHistory.value.pop()!
  const bet = placedBets.value.get(last.key)
  if (!bet) return
  const newMap = new Map(placedBets.value)
  const newAmt = bet.amountCents - last.chipCents
  if (newAmt <= 0) {
    newMap.delete(last.key)
  } else {
    newMap.set(last.key, { ...bet, amountCents: newAmt })
  }
  placedBets.value = newMap
}

function clearBets(): void {
  placedBets.value = new Map()
  betHistory.value = []
}

function rebet(): void {
  if (savedBets.value.size === 0 || spinning.value) return
  placedBets.value = new Map(savedBets.value)
  betHistory.value = []
  error.value = null
}

function doubleBets(): void {
  if (placedBets.value.size === 0 || spinning.value) return
  const newMap = new Map<string, PlacedBet>()
  let wouldExceedZone = false
  let wouldExceedTable = false
  let newTotal = 0

  for (const [key, bet] of placedBets.value.entries()) {
    const doubled = bet.amountCents * 2
    if (doubled > MAX_BET_PER_ZONE_CENTS) {
      wouldExceedZone = true
      break
    }
    newTotal += doubled
    newMap.set(key, { ...bet, amountCents: doubled })
  }

  if (wouldExceedZone) {
    error.value = `Double would exceed max $${(MAX_BET_PER_ZONE_CENTS / 100).toFixed(0)} per zone`
    return
  }

  if (TABLE_LIMIT_CENTS > 0 && newTotal > TABLE_LIMIT_CENTS) {
    wouldExceedTable = true
  }

  if (wouldExceedTable) {
    error.value = `Double would exceed table limit $${(TABLE_LIMIT_CENTS / 100).toFixed(0)}`
    return
  }

  placedBets.value = newMap
  betHistory.value = [] // undo history no longer valid after double
  error.value = null
}

const totalBetCents = computed<number>(() => {
  let total = 0
  for (const bet of placedBets.value.values()) {
    total += bet.amountCents
  }
  return total
})

const totalBetDisplay = computed<string>(() => {
  return formatCurrency(totalBetCents.value, rouletteCurrency.value)
})

const insufficientBalance = computed<boolean>(() => {
  return balanceCents.value > 0 && totalBetCents.value > balanceCents.value
})

const tableLimitExceeded = computed<boolean>(() => {
  return TABLE_LIMIT_CENTS > 0 && totalBetCents.value > TABLE_LIMIT_CENTS
})

const canSpin = computed<boolean>(() => {
  return !spinning.value && placedBets.value.size > 0 && !insufficientBalance.value && !tableLimitExceeded.value
})

/** Format a chip badge amount compactly. */
function fmtChip(cents: number): string {
  if (cents >= 100000) return `$${(cents / 100).toFixed(0)}`
  if (cents >= 100)   return `$${(cents / 100).toFixed(0)}`
  return `¢${cents}`
}

// ─── Number grid helpers ───────────────────────────────────────────────────────

/**
 * Returns the 3 numbers in a column group, top-to-bottom.
 * col 1..12; rowIdx 0=top, 1=mid, 2=bottom
 * top = col*3, mid = col*3-1, bottom = col*3-2
 */
function colCells(col: number): number[] {
  return [col * 3, col * 3 - 1, col * 3 - 2]
}

function streetNums(col: number): number[] {
  return [col * 3 - 2, col * 3 - 1, col * 3]
}

function sixlineNums(col: number): number[] {
  // six-line covering col and col+1
  return [...streetNums(col), ...streetNums(col + 1)]
}

function splitHNums(col: number, rowIdx: number): number[] {
  // horizontal split: same row, adjacent columns
  return [col * 3 - rowIdx, (col + 1) * 3 - rowIdx].sort((a, b) => a - b)
}

function splitVNums(col: number, rowIdx: number): number[] {
  // vertical split: same column, adjacent rows (rowIdx and rowIdx+1)
  const top = col * 3 - rowIdx
  const bottom = col * 3 - rowIdx - 1
  return [bottom, top]
}

function cornerNums(col: number, rowIdx: number): number[] {
  // 4 cells: (col,rowIdx), (col,rowIdx+1), (col+1,rowIdx), (col+1,rowIdx+1)
  return [
    col * 3 - rowIdx,
    col * 3 - rowIdx - 1,
    (col + 1) * 3 - rowIdx,
    (col + 1) * 3 - rowIdx - 1,
  ].sort((a, b) => a - b)
}

function colNums(colNum: number): number[] {
  // colNum 1=bottom row (n%3==1), 2=middle (n%3==2), 3=top (n%3==0)
  return Array.from({ length: 12 }, (_, i) => (i + 1) * 3 - (3 - colNum))
}

function dozenNums(d: number): number[] {
  return Array.from({ length: 12 }, (_, i) => (d - 1) * 12 + i + 1)
}

function lowNums(): number[] {
  return Array.from({ length: 18 }, (_, i) => i + 1)
}

function highNums(): number[] {
  return Array.from({ length: 18 }, (_, i) => i + 19)
}

function evenNums(): number[] {
  return Array.from({ length: 18 }, (_, i) => (i + 1) * 2)
}

function oddNums(): number[] {
  return Array.from({ length: 18 }, (_, i) => i * 2 + 1)
}

function redNums(): number[] {
  return [...RED_POCKETS]
}

function blackNums(): number[] {
  return Array.from({ length: 36 }, (_, i) => i + 1).filter(
    (n) => !RED_POCKETS.includes(n),
  )
}

// ─── App state ────────────────────────────────────────────────────────────────

interface HistoryEntry {
  pocket: number
  label: string
  color: 'red' | 'black' | 'green'
}

interface SpinResult {
  pocket: number
  label: string
  color: 'red' | 'black' | 'green'
  payoutCents: number
  betCents: number
}

const activeTab = ref<'bet' | 'stats' | 'rules'>('bet')
const balanceCents = ref<number>(0)
const rouletteCurrency = ref<string>('USD')
const spinning = ref<boolean>(false)
const resultVisible = ref<boolean>(false)
const lastResult = ref<SpinResult | null>(null)
const lastNetCents = ref<number>(0)
const history = ref<HistoryEntry[]>([])
const selectedHistoryIdx = ref<number | null>(null)
const error = ref<string | null>(null)

// ─── History constants ─────────────────────────────────────────────────────────

/** Maximum history entries stored in memory (for statistics). */
const HISTORY_MAX = 100

/** Maximum history dots shown in the wheel strip. */
const HISTORY_STRIP_MAX = 20

// ─── History strip entries (most recent HISTORY_STRIP_MAX) ─────────────────────

const historyStripEntries = computed<HistoryEntry[]>(() => history.value.slice(0, HISTORY_STRIP_MAX))

// ─── Statistics computed ───────────────────────────────────────────────────────

const pRed = computed<number>(() => {
  if (!history.value.length) return 0
  return (history.value.filter((e) => e.color === 'red').length / history.value.length) * 100
})

const pBlack = computed<number>(() => {
  if (!history.value.length) return 0
  return (history.value.filter((e) => e.color === 'black').length / history.value.length) * 100
})

const pGreen = computed<number>(() => {
  if (!history.value.length) return 0
  return (history.value.filter((e) => e.color === 'green').length / history.value.length) * 100
})

const numberFrequencies = computed<Map<number, number>>(() => {
  const freq = new Map<number, number>()
  for (const entry of history.value) {
    freq.set(entry.pocket, (freq.get(entry.pocket) ?? 0) + 1)
  }
  return freq
})

interface NumberStat {
  pocket: number
  label: string
  color: 'red' | 'black' | 'green'
  count: number
}

/** Top 10 most-frequently landed numbers. */
const hotNumbers = computed<NumberStat[]>(() => {
  if (!history.value.length) return []
  return [...numberFrequencies.value.entries()]
    .map(([pocket, count]) => ({
      pocket,
      label: pocket === 37 ? '00' : pocket.toString(),
      color: pocketColor(pocket),
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
})

/** Top 10 least-frequently landed numbers (including zeros, sorted by count asc). */
const coldNumbers = computed<NumberStat[]>(() => {
  if (!history.value.length) return []
  const freq = numberFrequencies.value
  const all37 = [0, ...Array.from({ length: 36 }, (_, i) => i + 1)]
  return all37
    .map((n) => ({
      pocket: n,
      label: n.toString(),
      color: pocketColor(n),
      count: freq.get(n) ?? 0,
    }))
    .sort((a, b) => a.count - b.count)
    .slice(0, 10)
})

// ─── Computed ─────────────────────────────────────────────────────────────────

const balanceDisplay = computed(() => {
  return formatCurrency(balanceCents.value, rouletteCurrency.value)
})

const resultColorName = computed(() => {
  if (!lastResult.value) return ''
  const c = lastResult.value.color
  return c === 'green' ? 'Green' : c === 'red' ? 'Red' : 'Black'
})

const resultAmountText = computed(() => {
  const net = lastNetCents.value
  if (net > 0) return `+$${(net / 100).toFixed(2)}`
  if (net < 0) return `-$${(Math.abs(net) / 100).toFixed(2)}`
  return 'Push'
})

// ─── Canvas refs ──────────────────────────────────────────────────────────────

const canvasWrap = ref<HTMLDivElement | null>(null)
const wheelCanvas = ref<HTMLCanvasElement | null>(null)
const markerOverlay = ref<HTMLDivElement | null>(null)

let ctx: CanvasRenderingContext2D | null = null
let animFrame: number = 0
let resizeObserver: ResizeObserver | null = null

// Current animation state
let animStartTime = 0
let animWheelStart = 0
let animWheelFinal = 0
let animBallStartAngle = 0
let animBallTotalAngle = 0
let animWinPocket = 0
let animRunning = false

// Static display state (when not animating)
let displayWheelAngle = 0
let displayBallAngle = 0
let displayBallRadius = 0
let displayWinPocket = -1  // -1 = none highlighted

// Canvas dimensions (logical, device-independent)
let canvasW = 0
let canvasH = 0
let wheelRadius = 0
let cx = 0
let cy = 0

// ─── Canvas setup ─────────────────────────────────────────────────────────────

function setupCanvas() {
  const wrap = canvasWrap.value
  const canvas = wheelCanvas.value
  if (!wrap || !canvas) return

  const dpr = window.devicePixelRatio || 1
  const rect = wrap.getBoundingClientRect()

  canvasW = rect.width
  canvasH = rect.height

  canvas.width = Math.round(canvasW * dpr)
  canvas.height = Math.round(canvasH * dpr)
  canvas.style.width = canvasW + 'px'
  canvas.style.height = canvasH + 'px'

  ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.scale(dpr, dpr)
  }

  // Wheel geometry: use the smaller dimension, leave room for marker and history
  const historyH = 48
  const availableH = canvasH - historyH
  const minDim = Math.min(canvasW, availableH)
  wheelRadius = minDim * 0.42

  cx = canvasW / 2
  cy = availableH / 2

  // Position marker overlay
  if (markerOverlay.value) {
    const markerTop = cy - wheelRadius - 18
    markerOverlay.value.style.left = cx + 'px'
    markerOverlay.value.style.top = markerTop + 'px'
  }

  // Initialize ball display position (top of wheel, outer track)
  displayBallAngle = -Math.PI / 2
  displayBallRadius = wheelRadius * BALL_TRACK_FRAC

  drawFrame(displayWheelAngle, displayBallAngle, displayBallRadius, displayWinPocket)
}

// ─── Drawing ──────────────────────────────────────────────────────────────────

function drawFrame(
  wheelAngle: number,
  ballAngle: number,
  ballRadius: number,
  highlightPocket: number,
) {
  if (!ctx) return

  ctx.clearRect(0, 0, canvasW, canvasH)

  const n = EUROPEAN_ORDER.length
  const sectorAngle = (2 * Math.PI) / n

  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(wheelAngle)

  // ── 1. Draw sectors ────────────────────────────────────────────────────────
  const outerR = wheelRadius
  const innerR = wheelRadius * 0.55

  for (let i = 0; i < n; i++) {
    const pocket = EUROPEAN_ORDER[i]
    const startAngle = -Math.PI / 2 + i * sectorAngle
    const endAngle = startAngle + sectorAngle

    // Sector fill
    ctx.beginPath()
    ctx.arc(0, 0, outerR, startAngle, endAngle)
    ctx.arc(0, 0, innerR, endAngle, startAngle, true)
    ctx.closePath()

    // Highlight winning pocket
    if (highlightPocket >= 0 && pocket === highlightPocket) {
      ctx.fillStyle = '#FFD700'
    } else {
      ctx.fillStyle = SECTOR_COLORS[pocketColor(pocket)]
    }
    ctx.fill()

    // Sector border
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.25)'
    ctx.lineWidth = 0.8
    ctx.stroke()
  }

  // ── 2. Draw numbers ────────────────────────────────────────────────────────
  const numRadius = (outerR + innerR) * 0.5
  const fontSize = Math.max(9, Math.round(wheelRadius * 0.065))
  ctx.font = `bold ${fontSize}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  for (let i = 0; i < n; i++) {
    const pocket = EUROPEAN_ORDER[i]
    const midAngle = -Math.PI / 2 + i * sectorAngle + sectorAngle / 2

    ctx.save()
    ctx.rotate(midAngle)
    ctx.translate(numRadius, 0)
    ctx.rotate(Math.PI / 2)

    // Number color: gold on highlighted, white otherwise
    if (highlightPocket >= 0 && pocket === highlightPocket) {
      ctx.fillStyle = '#000'
    } else {
      ctx.fillStyle = '#fff'
    }
    ctx.fillText(pocket.toString(), 0, 0)
    ctx.restore()
  }

  // ── 3. Inner decorative ring ───────────────────────────────────────────────
  const coneR = innerR

  // Inner wheel (static cone area): dark background
  ctx.beginPath()
  ctx.arc(0, 0, coneR, 0, 2 * Math.PI)
  ctx.fillStyle = '#1a2c38'
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,215,0,0.4)'
  ctx.lineWidth = 1.5
  ctx.stroke()

  // Decorative spokes
  const spokeCount = 8
  for (let s = 0; s < spokeCount; s++) {
    const angle = (s / spokeCount) * 2 * Math.PI
    ctx.beginPath()
    ctx.moveTo(Math.cos(angle) * coneR * 0.15, Math.sin(angle) * coneR * 0.15)
    ctx.lineTo(Math.cos(angle) * coneR * 0.85, Math.sin(angle) * coneR * 0.85)
    ctx.strokeStyle = 'rgba(255,215,0,0.15)'
    ctx.lineWidth = 1
    ctx.stroke()
  }

  // Center boss
  const bossR = wheelRadius * 0.12
  ctx.beginPath()
  ctx.arc(0, 0, bossR, 0, 2 * Math.PI)
  const bossGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, bossR)
  bossGrad.addColorStop(0, '#FFD700')
  bossGrad.addColorStop(0.5, '#cc9900')
  bossGrad.addColorStop(1, '#886600')
  ctx.fillStyle = bossGrad
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,215,0,0.8)'
  ctx.lineWidth = 1.5
  ctx.stroke()

  ctx.restore()

  // ── 4. Outer wheel border (non-rotating) ───────────────────────────────────
  ctx.save()
  ctx.translate(cx, cy)

  ctx.beginPath()
  ctx.arc(0, 0, outerR + 4, 0, 2 * Math.PI)
  ctx.strokeStyle = 'rgba(255,215,0,0.5)'
  ctx.lineWidth = 3
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(0, 0, outerR + 8, 0, 2 * Math.PI)
  ctx.strokeStyle = 'rgba(255,215,0,0.2)'
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.restore()

  // ── 5. Ball ────────────────────────────────────────────────────────────────
  const ballX = cx + Math.cos(ballAngle) * ballRadius
  const ballY = cy + Math.sin(ballAngle) * ballRadius
  const ballR = Math.max(5, wheelRadius * 0.04)

  ctx.save()
  const ballGrad = ctx.createRadialGradient(
    ballX - ballR * 0.3, ballY - ballR * 0.3, 0,
    ballX, ballY, ballR,
  )
  ballGrad.addColorStop(0, '#ffffff')
  ballGrad.addColorStop(0.4, '#e0e0e0')
  ballGrad.addColorStop(1, '#aaaaaa')

  ctx.beginPath()
  ctx.arc(ballX, ballY, ballR, 0, 2 * Math.PI)
  ctx.fillStyle = ballGrad
  ctx.fill()
  ctx.shadowColor = 'rgba(255,255,255,0.6)'
  ctx.shadowBlur = 6
  ctx.strokeStyle = 'rgba(255,255,255,0.8)'
  ctx.lineWidth = 0.5
  ctx.stroke()
  ctx.restore()
}

// ─── Animation loop ───────────────────────────────────────────────────────────

function animationTick(timestamp: number) {
  if (!animRunning) return

  const elapsed = timestamp - animStartTime
  const t = Math.min(1, elapsed / SPIN_DURATION_MS)

  // ── Wheel: clockwise rotation, easeOutQuart ────────────────────────────────
  const wheelT = easeOutQuart(t)
  const wheelAngle = animWheelStart + (animWheelFinal - animWheelStart) * wheelT

  // ── Ball: counterclockwise rotation, easeOutCubic ─────────────────────────
  const ballT = easeOutCubic(t)
  const ballAngle = animBallStartAngle - animBallTotalAngle * ballT

  // ── Ball radius: stays outer until 75%, then easeOutBack to pocket ─────────
  let ballRadius: number
  const radiusT = t < 0.75 ? 0 : (t - 0.75) / 0.25
  const outerBallR = wheelRadius * BALL_TRACK_FRAC
  const innerBallR = wheelRadius * BALL_POCKET_FRAC

  if (radiusT <= 0) {
    ballRadius = outerBallR
  } else {
    // Small bounce as ball settles
    const settled = easeOutBack(Math.min(1, radiusT), 0.3)
    ballRadius = outerBallR + (innerBallR - outerBallR) * settled
  }

  // Update display state
  displayWheelAngle = wheelAngle
  displayBallAngle = ballAngle
  displayBallRadius = ballRadius

  // Highlight winning pocket only after ball is settling (t > 0.85)
  const highlight = t > 0.85 ? animWinPocket : -1

  drawFrame(wheelAngle, ballAngle, ballRadius, highlight)

  if (t < 1) {
    animFrame = requestAnimationFrame(animationTick)
  } else {
    // Animation complete
    animRunning = false
    displayWinPocket = animWinPocket
    drawFrame(wheelAngle, -Math.PI / 2, innerBallR, animWinPocket)
    onAnimationComplete()
  }
}

function startWheelAnimation(winPocket: number) {
  cancelAnimationFrame(animFrame)

  const n = EUROPEAN_ORDER.length
  const winIndex = EUROPEAN_ORDER.indexOf(winPocket)

  // Final wheel angle (clockwise): add WHEEL_ROTATIONS full rotations +
  // the extra rotation needed to bring winIndex pocket CENTER to 12 o'clock.
  const sectorAngle = (2 * Math.PI) / n
  const targetAngle = (2 * Math.PI - (winIndex + 0.5) * sectorAngle) % (2 * Math.PI)

  animWheelStart = displayWheelAngle % (2 * Math.PI) // start from current angle
  const extraRotation = (targetAngle - animWheelStart + 2 * Math.PI) % (2 * Math.PI)
  animWheelFinal = animWheelStart + WHEEL_ROTATIONS * 2 * Math.PI + extraRotation

  // Ball animation: starts at current ball angle, moves counterclockwise
  animBallStartAngle = -Math.PI / 2 // always start at top for simplicity
  const totalCCW = BALL_ROTATIONS * 2 * Math.PI
  animBallTotalAngle = totalCCW

  animWinPocket = winPocket
  animRunning = true
  animStartTime = performance.now()

  animFrame = requestAnimationFrame(animationTick)
}

// ─── API call ─────────────────────────────────────────────────────────────────

async function doSpin() {
  if (!canSpin.value) return

  if (insufficientBalance.value) {
    error.value = t('games.insufficient_balance')
    return
  }

  error.value = null
  spinning.value = true
  resultVisible.value = false
  lastResult.value = null

  // Save current bets for rebet
  savedBets.value = new Map(placedBets.value)

  const betsArray = [...placedBets.value.values()].map((b) => ({
    type: b.type,
    numbers: b.numbers,
    amount_cents: b.amountCents,
  }))
  const totalDollars = totalBetCents.value / 100

  try {
    const response = await apiClient.post<{
      payout: number
      multiplier: number
      is_win: boolean
      outcome_data: {
        pocket: number
        label: string
        color: string
        variant: string
      }
    }>('/api/games/roulette/play', {
      amount: totalDollars.toFixed(2),
      options: { bets: betsArray },
    })

    const data = response.data
    const outcomeData = data.outcome_data
    const pocket = outcomeData.pocket
    const label = outcomeData.label
    const color = outcomeData.color as 'red' | 'black' | 'green'

    const payoutCents = Math.round(data.payout * 100)
    const netCents = payoutCents - totalBetCents.value

    lastResult.value = { pocket, label, color, payoutCents, betCents: totalBetCents.value }
    lastNetCents.value = netCents

    // Update balance (fetch fresh balance after spin)
    await fetchBalance()

    // Add to history (most recent first, capped at HISTORY_MAX for statistics)
    history.value.unshift({ pocket, label, color })
    if (history.value.length > HISTORY_MAX) history.value.pop()

    // Start wheel animation
    startWheelAnimation(pocket)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } }; message?: string }
    error.value = e.response?.data?.message ?? e.message ?? 'Spin failed'
    spinning.value = false
  }
}

function onAnimationComplete() {
  spinning.value = false
  // Small delay before showing overlay so player can see the landing pocket
  setTimeout(() => {
    resultVisible.value = true
  }, 600)
}

function resetState() {
  resultVisible.value = false
  // Keep bets on table (player can rebet or modify)
  // Reset win highlight after a moment
  setTimeout(() => {
    displayWinPocket = -1
    drawFrame(displayWheelAngle, displayBallAngle, displayBallRadius, -1)
  }, 300)
}

// ─── Balance ──────────────────────────────────────────────────────────────────

async function fetchBalance() {
  try {
    const res = await apiClient.get<{ balances: Array<{ currency: string; balance: number }> }>(
      '/api/wallet/balances',
    )
    const usd = res.data.balances?.find((b) => b.currency === 'USD')
    if (usd) {
      balanceCents.value = Math.round(usd.balance * 100)
      rouletteCurrency.value = usd.currency
    }
  } catch {
    // Ignore balance fetch errors
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  fetchBalance()

  resizeObserver = new ResizeObserver(() => {
    setupCanvas()
  })

  if (canvasWrap.value) {
    resizeObserver.observe(canvasWrap.value)
  }

  // Initial setup after first render
  setTimeout(setupCanvas, 10)
})

onBeforeUnmount(() => {
  cancelAnimationFrame(animFrame)
  resizeObserver?.disconnect()
})
</script>

<style scoped>
/* ── Layout shell ────────────────────────────────────────────────────────────── */
.roulette-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg-primary, #0f1923);
  color: var(--text-primary, #fff);
  font-family: var(--font-body, 'DM Sans', sans-serif);
  overflow: hidden;
}

/* ── Topbar ─────────────────────────────────────────────────────────────────── */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}
.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
}
.btn-back:hover { color: var(--color-on-dark, #fff); background: var(--bg-hover, #2a4455); }
.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}
.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
}
.roulette-tag {
  background: rgba(255, 215, 0, 0.1);
  color: var(--accent-gold, #ffd700);
  border: 1px solid rgba(255, 215, 0, 0.2);
}
.game-topbar-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.icon-btn {
  width: 32px; height: 32px; border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; cursor: pointer;
  color: var(--text-secondary, #b1bad3); border: none;
  transition: all 150ms ease;
}
.icon-btn:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

/* ── Game body ──────────────────────────────────────────────────────────────── */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ── Main area (left: wheel + table) ───────────────────────────────────────── */
.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* ── Wheel section ──────────────────────────────────────────────────────────── */
.wheel-section {
  flex: 0 0 260px;
  background: var(--bg-game, #0d1b2a);
  position: relative;
  overflow: hidden;
}

.wheel-canvas {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
}

/* ── Marker triangle (always at top center of wheel) ────────────────────────── */
.wheel-marker-overlay {
  position: absolute;
  transform: translateX(-50%);
  z-index: 5;
  pointer-events: none;
}
.wheel-marker-triangle {
  width: 0; height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 18px solid var(--accent-gold, #ffd700);
  filter: drop-shadow(0 0 4px rgba(255, 215, 0, 0.8));
}

/* ── Result overlay ─────────────────────────────────────────────────────────── */
.result-overlay {
  position: absolute;
  inset: 0;
  bottom: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(13, 27, 42, 0.88);
  backdrop-filter: blur(6px);
  z-index: 10;
  gap: 12px;
}
.result-number-badge {
  width: 72px; height: 72px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 28px; font-weight: 900;
  border: 3px solid rgba(255, 255, 255, 0.25);
  animation: win-pop 0.4s ease both;
}
.result-number-badge.red { background: var(--roulette-red-dark, #b71c1c); color: var(--color-on-dark, #fff); }
.result-number-badge.black { background: var(--roulette-black-deep, #1a1a1a); color: var(--color-on-dark, #fff); border-color: rgba(255,255,255,0.3); }
.result-number-badge.green { background: var(--roulette-green-dark, #1b6b1b); color: var(--color-on-dark, #fff); }
@keyframes win-pop {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.result-color-name { font-size: 14px; color: var(--text-secondary, #b1bad3); text-transform: uppercase; letter-spacing: 0.1em; }
.result-amount { font-family: var(--font-mono, monospace); font-size: 24px; font-weight: 700; }
.result-amount.win { color: var(--accent-primary, #00e701); }
.result-amount.loss { color: var(--color-danger, #ed4245); }
.result-amount.push { color: var(--accent-gold, #ffd700); }
.btn-spin-again {
  padding: 11px 28px;
  background: var(--accent-primary, #00e701); color: var(--color-on-accent, #000);
  font-size: 14px; font-weight: 800; border-radius: 8px; border: none;
  cursor: pointer; transition: all 150ms ease; letter-spacing: 0.02em;
}
.btn-spin-again:hover { background: var(--accent-primary-hover, #00cc01); box-shadow: 0 0 20px rgba(0, 231, 1, 0.4); }

/* ── Overlay transition ─────────────────────────────────────────────────────── */
.overlay-fade-enter-active, .overlay-fade-leave-active { transition: opacity 0.3s ease; }
.overlay-fade-enter-from, .overlay-fade-leave-to { opacity: 0; }

/* ── History strip ──────────────────────────────────────────────────────────── */
.history-strip {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 44px;
  display: flex; align-items: center; gap: 4px;
  padding: 0 12px;
  overflow-x: auto;
  flex-direction: row-reverse;
  z-index: 6;
  background: rgba(11, 21, 32, 0.7);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.history-strip::-webkit-scrollbar { display: none; }
.hist-dot {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700; cursor: default;
  transition: transform 150ms ease;
}
.hist-dot:hover { transform: scale(1.15); }
.hist-dot.red { background: var(--roulette-red-dark, #b71c1c); color: var(--color-on-dark, #fff); }
.hist-dot.black { background: var(--roulette-black-deep, #222); color: var(--color-on-dark, #fff); border: 1px solid rgba(255,255,255,0.2); }
.hist-dot.green { background: var(--roulette-green-dark, #1b6b1b); color: var(--color-on-dark, #fff); }

/* ── Betting table section ──────────────────────────────────────────────────── */
.table-section {
  flex: 1;
  background: var(--roulette-green-deep, #0b2010);
  overflow: auto;
  padding: 8px;
  min-height: 0;
}

/* ── Betting table grid ─────────────────────────────────────────────────────── */
.betting-table {
  display: grid;
  grid-template-columns: 34px repeat(12, minmax(28px, 1fr)) 34px;
  grid-template-rows: 16px 38px 38px 38px 28px 28px;
  gap: 0;
  min-width: 480px;
  height: 100%;
  min-height: 170px;
  border: 1px solid rgba(255, 215, 0, 0.2);
}

/* ── Shared bet cell style ──────────────────────────────────────────────────── */
.bet-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 215, 0, 0.15);
  cursor: pointer;
  user-select: none;
  transition: background 100ms ease;
  overflow: visible;
}
.bet-cell:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 215, 0, 0.35);
  z-index: 1;
}

/* ── Zero cell ──────────────────────────────────────────────────────────────── */
.zero-cell {
  background: var(--roulette-green-dark, #1b5e20);
  border-color: rgba(255, 215, 0, 0.3);
}
.zero-cell:hover { background: var(--roulette-green-hover, #2e7d32); }

/* ── Cell labels ────────────────────────────────────────────────────────────── */
.cell-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-on-dark, #fff);
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  pointer-events: none;
  position: relative;
  z-index: 0;
}
.cell-label.sm { font-size: 10px; }
.cell-label.green { color: var(--color-on-dark, #fff); }

/* ── Number cells ───────────────────────────────────────────────────────────── */
.number-cell {
  font-size: 12px;
  font-weight: 700;
}
.number-cell.red { background: var(--roulette-red-deep, #7f1010); }
.number-cell.black { background: var(--roulette-black-deep, #1a1a1a); }
.number-cell.green { background: var(--roulette-green-dark, #1b5e20); }

.number-cell.red:hover { background: var(--roulette-red-dark, #9b1a1a); }
.number-cell.black:hover { background: var(--roulette-black, #2a2a2a); }
.number-cell.green:hover { background: var(--roulette-green-hover, #2e7d32); }

/* ── Chip badge (shown on top of the cell) ──────────────────────────────────── */
.chip-badge {
  position: absolute;
  z-index: 4;
  background: var(--accent-gold, #ffd700);
  color: var(--color-on-accent, #000);
  border-radius: 50%;
  width: 22px; height: 22px;
  display: flex; align-items: center; justify-content: center;
  font-size: 7px; font-weight: 900;
  border: 2px solid rgba(0,0,0,0.4);
  box-shadow: 0 2px 6px rgba(0,0,0,0.5);
  pointer-events: none;
  line-height: 1;
}
.chip-badge.sm { width: 16px; height: 16px; font-size: 6px; }
.chip-badge.xs { width: 12px; height: 12px; font-size: 6px; }

/* ── Split zones (inside number cells, flush with borders) ──────────────────── */
.zone-right {
  position: absolute;
  right: 0; top: 0; bottom: 0;
  width: 10px;
  z-index: 3;
  cursor: crosshair;
  display: flex; align-items: center; justify-content: center;
}
.zone-right:hover { background: rgba(255, 215, 0, 0.3); }

.zone-bottom {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 10px;
  z-index: 3;
  cursor: crosshair;
  display: flex; align-items: center; justify-content: center;
}
.zone-bottom:hover { background: rgba(255, 215, 0, 0.3); }

.zone-corner {
  position: absolute;
  right: 0; bottom: 0;
  width: 10px; height: 10px;
  z-index: 4;
  cursor: crosshair;
  display: flex; align-items: center; justify-content: center;
}
.zone-corner:hover { background: rgba(255, 215, 0, 0.5); border-radius: 2px; }

/* ── Street zone row (top of number grid) ───────────────────────────────────── */
.street-zone {
  position: relative;
  background: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.12);
  cursor: crosshair;
  display: flex; align-items: center; justify-content: center;
  overflow: visible;
}
.street-zone:hover { background: rgba(255, 215, 0, 0.15); }

/* Six-line zone (right edge of street zone, inside bounds) */
.sixline-zone {
  position: absolute;
  right: 0; top: 0; bottom: 0;
  width: 10px;
  z-index: 5;
  cursor: crosshair;
  display: flex; align-items: center; justify-content: center;
}
.sixline-zone:hover { background: rgba(0, 231, 1, 0.35); }

/* ── Column bet cells ───────────────────────────────────────────────────────── */
.col-bet-cell {
  background: rgba(255, 215, 0, 0.06);
  writing-mode: vertical-lr;
  text-orientation: mixed;
  font-size: 10px;
}
.col-bet-cell:hover { background: rgba(255, 215, 0, 0.15); }

/* ── Dozen cells ────────────────────────────────────────────────────────────── */
.dozen-cell {
  background: rgba(255, 255, 255, 0.04);
}
.dozen-cell:hover { background: rgba(255, 255, 255, 0.1); }

/* ── Outside (even-money) cells ─────────────────────────────────────────────── */
.outside-cell {
  background: rgba(255, 255, 255, 0.04);
}
.outside-cell:hover { background: rgba(255, 255, 255, 0.1); }
.outside-red { background: rgba(183, 28, 28, 0.35) !important; }
.outside-red:hover { background: rgba(183, 28, 28, 0.55) !important; }
.outside-black { background: rgba(20, 20, 20, 0.6) !important; }
.outside-black:hover { background: rgba(30, 30, 30, 0.8) !important; }

.red-diamond { color: var(--roulette-red, #ff4444); font-size: 18px; pointer-events: none; }
.black-diamond { color: var(--color-muted, #888); font-size: 18px; pointer-events: none; }

/* ── Sidebar ────────────────────────────────────────────────────────────────── */
.game-sidebar {
  width: 240px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px; font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center; cursor: pointer;
  border-bottom: 2px solid transparent;
  border-top: none; border-left: none; border-right: none;
  background: none;
  transition: all 150ms ease;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.game-tab.active { color: var(--accent-primary, #00e701); border-bottom-color: var(--accent-primary, #00e701); }
.game-tab:hover:not(.active) { color: var(--text-secondary, #b1bad3); }

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }
.game-tab-content::-webkit-scrollbar-thumb { background: var(--bg-tertiary, #213743); border-radius: 4px; }

/* ── Chip selector ──────────────────────────────────────────────────────────── */
.ctrl-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  display: block; margin-bottom: 0;
}

.chip-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.chip {
  width: 40px; height: 40px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 800;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 150ms ease;
  flex-shrink: 0;
}
.chip:hover:not(:disabled), .chip.selected {
  transform: scale(1.12);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
}
.chip.selected { border-color: var(--color-on-dark, #fff) !important; box-shadow: 0 0 16px rgba(255,255,255,0.5) !important; }
.chip:disabled { opacity: 0.4; cursor: not-allowed; }
.chip-1   { background: linear-gradient(135deg, #4a4a4a, #2a2a2a); color: var(--color-on-dark, #fff); }
.chip-5   { background: linear-gradient(135deg, #1475e1, #0d5abf); color: var(--color-on-dark, #fff); }
.chip-25  { background: linear-gradient(135deg, #00e701, #00aa00); color: var(--color-on-accent, #000); }
.chip-100 { background: linear-gradient(135deg, #ffd700, #cc9900); color: var(--color-on-accent, #000); }
.chip-500 { background: linear-gradient(135deg, var(--accent-purple, #9b59b6), #7d3f9a); color: var(--color-on-dark, #fff); }
.chip-1k  { background: linear-gradient(135deg, #e74c3c, #c0392b); color: var(--color-on-dark, #fff); }

/* ── Stat pills ─────────────────────────────────────────────────────────────── */
.stat-pills { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.stat-pill { background: var(--bg-tertiary, #213743); border-radius: 8px; padding: 8px 10px; border: 1px solid rgba(255,255,255,0.04); }
.sp-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: var(--text-tertiary, #7f8fa4); margin-bottom: 3px; }
.sp-value { font-family: var(--font-mono, monospace); font-size: 13px; font-weight: 700; color: var(--accent-primary, #00e701); }
.sp-value.gold { color: var(--accent-gold, #ffd700); }

/* ── Undo / Clear / Rebet buttons ───────────────────────────────────────────── */
.action-row { display: flex; gap: 4px; }
.act-btn {
  flex: 1;
  padding: 7px 4px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px; font-weight: 700;
  border-radius: 6px; border: none;
  cursor: pointer; transition: all 150ms ease;
  text-align: center;
}
.act-btn:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.act-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.act-btn-danger { color: var(--color-danger, #ed4245); }
.act-btn-danger:hover:not(:disabled) { background: rgba(237, 66, 69, 0.15); color: var(--color-danger, #ed4245); }
.act-btn-gold { color: var(--accent-gold, #ffd700); }
.act-btn-gold:hover:not(:disabled) { background: rgba(255, 215, 0, 0.1); color: var(--accent-gold, #ffd700); }
.act-btn-double { color: var(--color-cyan, #00e7ff); font-weight: 900; }
.act-btn-double:hover:not(:disabled) { background: rgba(0, 231, 255, 0.1); color: var(--color-cyan, #00e7ff); }

/* ── Warning / Error messages ───────────────────────────────────────────────── */
.warn-msg {
  font-size: 12px; color: var(--color-warning, #ffa500);
  padding: 8px;
  background: rgba(255, 165, 0, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(255, 165, 0, 0.25);
}
.error-msg {
  font-size: 12px; color: var(--color-danger, #ed4245);
  padding: 8px;
  background: rgba(237, 66, 69, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* ── Table limit indicator ──────────────────────────────────────────────────── */
.limit-bar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 5px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.limit-label { font-size: 10px; color: var(--text-tertiary, #7f8fa4); text-transform: uppercase; letter-spacing: .06em; }
.limit-value { font-family: var(--font-mono, monospace); font-size: 11px; font-weight: 700; color: var(--text-secondary, #b1bad3); }
.limit-value.limit-exceeded { color: var(--color-warning, #ffa500); }

/* ── Spin button ────────────────────────────────────────────────────────────── */
.btn-action.spin-btn {
  width: 100%; padding: 14px;
  background: var(--accent-primary, #00e701); color: var(--color-on-accent, #000);
  font-size: 15px; font-weight: 800;
  border-radius: 8px; border: none;
  cursor: pointer; transition: all 150ms ease;
  letter-spacing: 0.03em;
}
.btn-action.spin-btn:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}
.btn-action.spin-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; transform: none; }

.spin-loading { display: inline-block; animation: spin-label 1s linear infinite; }
@keyframes spin-label { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }

/* ── Balance row ────────────────────────────────────────────────────────────── */
.balance-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}
.bal-label { font-size: 11px; color: var(--text-tertiary, #7f8fa4); }
.bal-value { font-family: var(--font-mono, monospace); font-size: 13px; font-weight: 700; }
.bal-value.green { color: var(--accent-primary, #00e701); }

/* ── Rules tab ──────────────────────────────────────────────────────────────── */
.rules-section { margin-bottom: 16px; }
.rules-title {
  font-size: 12px; font-weight: 700; color: var(--color-on-dark, #fff);
  margin-bottom: 8px; display: flex; align-items: center; gap: 6px;
  padding-bottom: 6px; border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.rules-title-icon { font-size: 14px; }
.rules-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
.rules-item {
  font-size: 11px; color: var(--text-secondary, #b1bad3);
  padding-left: 14px; position: relative; line-height: 1.55;
}
.rules-item::before { content: '▸'; position: absolute; left: 0; color: var(--accent-primary, #00e701); font-size: 10px; top: 1px; }
.rules-note {
  font-size: 11px; color: var(--text-tertiary, #7f8fa4);
  background: var(--bg-tertiary, #213743); border-radius: 8px;
  padding: 10px; margin-top: 10px; line-height: 1.6;
  border-left: 2px solid var(--accent-primary, #00e701);
}
.fair-tag {
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(20, 117, 225, 0.1); border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: 9999px; padding: 4px 10px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.05em; color: var(--accent-secondary, #1475e1);
  cursor: pointer;
}

/* ── View toggle bar ────────────────────────────────────────────────────────── */
.view-toggle-bar {
  flex-shrink: 0;
  display: flex;
  gap: 4px;
  background: var(--roulette-bg-panel, #0b1e30);
  border-bottom: 1px solid rgba(255, 215, 0, 0.15);
  padding: 5px 8px;
}
.view-btn {
  flex: 1;
  padding: 6px 12px;
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  cursor: pointer;
  transition: all 150ms ease;
}
.view-btn:hover:not(.active) { color: var(--color-on-dark, #fff); background: rgba(255, 255, 255, 0.06); }
.view-btn.active {
  color: var(--accent-gold, #ffd700);
  background: rgba(255, 215, 0, 0.1);
  border-color: rgba(255, 215, 0, 0.3);
}

/* ── Racetrack section ──────────────────────────────────────────────────────── */
.racetrack-section {
  flex: 1;
  background: var(--roulette-bg-table, #071522);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 8px 8px;
  gap: 10px;
  overflow: auto;
  min-height: 0;
}

/* ── Preset zone buttons ────────────────────────────────────────────────────── */
.track-presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  flex-shrink: 0;
}
.preset-btn {
  padding: 7px 14px;
  font-size: 11px; font-weight: 800;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  min-width: 90px;
}
.preset-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.preset-voisins {
  background: rgba(194, 24, 91, 0.15);
  color: var(--roulette-voisins-light, #f48fb1);
  border: 1px solid rgba(194, 24, 91, 0.4);
}
.preset-voisins:hover:not(:disabled) { background: rgba(194, 24, 91, 0.35); color: var(--color-on-dark, #fff); }
.preset-tiers {
  background: rgba(21, 101, 192, 0.15);
  color: var(--roulette-tiers-light, #90caf9);
  border: 1px solid rgba(21, 101, 192, 0.4);
}
.preset-tiers:hover:not(:disabled) { background: rgba(21, 101, 192, 0.35); color: var(--color-on-dark, #fff); }
.preset-orphelins {
  background: rgba(106, 27, 154, 0.15);
  color: var(--roulette-orphelins-light, #ce93d8);
  border: 1px solid rgba(106, 27, 154, 0.4);
}
.preset-orphelins:hover:not(:disabled) { background: rgba(106, 27, 154, 0.35); color: var(--color-on-dark, #fff); }

/* ── Racetrack oval ─────────────────────────────────────────────────────────── */
.racetrack-oval {
  width: 100%;
  max-width: 640px;
  background: var(--roulette-bg-oval, #0a1c2d);
  border: 2px solid rgba(255, 215, 0, 0.35);
  border-radius: 28px;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}
.track-row {
  display: flex;
  justify-content: center;
  gap: 2px;
}
.track-num {
  width: 28px; height: 28px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 8px; font-weight: 800;
  cursor: pointer;
  border: 1.5px solid rgba(255, 255, 255, 0.15);
  transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
  flex-shrink: 0;
  position: relative;
}
.track-num:hover:not(:disabled) { transform: scale(1.18); z-index: 2; }
.track-num:disabled { cursor: not-allowed; }

/* ── Racetrack legend ───────────────────────────────────────────────────────── */
.track-legend {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.legend-items {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.leg-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: var(--text-secondary, #b1bad3);
}
.leg-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.leg-tip {
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
}

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .game-sidebar { width: 200px; }
  .wheel-section { flex: 0 0 220px; }
}

@media (max-width: 640px) {
  .game-sidebar { display: none; }
  .wheel-section { flex: 0 0 200px; }
  .table-section { padding: 4px; }
  .track-num { width: 24px; height: 24px; font-size: 7px; }
}

/* ── History dot: clickable + selected state ────────────────────────────────── */
.hist-dot { cursor: pointer !important; }
.hist-dot-selected {
  outline: 2px solid var(--accent-gold, #ffd700);
  outline-offset: 2px;
  transform: scale(1.2) !important;
}

/* ── History entry detail bar ────────────────────────────────────────────────── */
.hist-detail-bar {
  position: absolute;
  bottom: 44px; left: 0; right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  background: rgba(11, 21, 32, 0.92);
  border-top: 1px solid rgba(255, 215, 0, 0.2);
  font-size: 11px;
  z-index: 7;
}
.hist-detail-badge {
  width: 24px; height: 24px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 800;
  flex-shrink: 0;
}
.hist-detail-badge.red   { background: var(--roulette-red-dark, #b71c1c); color: var(--color-on-dark, #fff); }
.hist-detail-badge.black { background: var(--roulette-black-deep, #222); color: var(--color-on-dark, #fff); border: 1px solid rgba(255,255,255,0.2); }
.hist-detail-badge.green { background: var(--roulette-green-dark, #1b6b1b); color: var(--color-on-dark, #fff); }
.hist-detail-info {
  flex: 1;
  color: var(--text-secondary, #b1bad3);
  text-transform: capitalize;
}
.hist-detail-close {
  background: none; border: none;
  color: var(--text-tertiary, #7f8fa4);
  cursor: pointer; font-size: 16px; padding: 0 2px; line-height: 1;
}
.hist-detail-close:hover { color: var(--color-on-dark, #fff); }
.hist-detail-fade-enter-active,
.hist-detail-fade-leave-active { transition: all 200ms ease; }
.hist-detail-fade-enter-from,
.hist-detail-fade-leave-to { opacity: 0; transform: translateY(4px); }

/* ── Stats tab ───────────────────────────────────────────────────────────────── */
.stats-empty {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
  text-align: center;
  padding: 24px 8px;
  line-height: 1.6;
}
.stats-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
.stats-section-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 5px;
  padding-top: 4px;
}
.stats-color-row {
  display: flex;
  gap: 5px;
  margin-bottom: 8px;
}
.stats-color-pill {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 4px;
  border: 1px solid rgba(255,255,255,0.04);
}
.scp-color-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.scp-label {
  font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
}
.scp-value {
  font-family: var(--font-mono, monospace);
  font-size: 12px; font-weight: 700; color: var(--color-on-dark, #fff);
}
.stats-num-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}
.stats-num-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  width: 35px;
  padding: 5px 2px;
  border-radius: 6px;
  border: 1.5px solid rgba(255,255,255,0.1);
  cursor: default;
}
.stats-num-item.red   { background: rgba(183, 28, 28, 0.25); border-color: rgba(183, 28, 28, 0.4); }
.stats-num-item.black { background: rgba(20, 20, 20, 0.55); border-color: rgba(255,255,255,0.12); }
.stats-num-item.green { background: rgba(27, 107, 27, 0.25); border-color: rgba(27, 107, 27, 0.4); }
.stats-num-cold { opacity: 0.65; }
.sni-label {
  font-size: 11px; font-weight: 800; color: var(--color-on-dark, #fff); line-height: 1;
}
.sni-count {
  font-size: 8px; font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
}
.stats-disclaimer {
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 2px solid rgba(255, 165, 0, 0.4);
  border-radius: 8px;
  padding: 10px;
  line-height: 1.6;
  margin-top: 4px;
}
</style>
